export interface User {
	id: string;
	name: string;
	profilePicture?: string | undefined;
}

export interface RoadmapItem {
	id: string;
	title: string;
	description: string;
	status: "Planned" | "In Progress" | "Completed" | "On Hold";
	priority: "high" | "medium" | "low";
	category: string;
	created_at: number; // unix timestamp
	target_quarter?: string; // e.g., "2024-Q2"
	upvotes: RoadmapItemUpvote[];
	owner?: User;
	metadata: {
		comment_count: number;
		last_updated: number; // unix timestamp
		tags: string[];
	};
}

export const roadmapItems: RoadmapItem[] = [
	{
		id: "feat-001",
		title: "Real-time Collaboration",
		description:
			"Implement a robust real-time collaboration system that enables multiple users to simultaneously edit documents with intelligent conflict resolution. This feature will include:\n\n" +
			"- Operational Transform (OT) based conflict resolution system\n" +
			"- Presence awareness showing who is currently viewing/editing\n" +
			"- Real-time cursor and selection synchronization\n" +
			"- Automatic conflict detection and resolution\n" +
			"- Offline support with automatic sync when connection is restored\n\n" +
			"The system will handle edge cases such as network disconnections, concurrent edits, and merge conflicts while maintaining data consistency. Performance optimizations will ensure smooth collaboration even with large documents and many concurrent users.\n\n" +
			"Technical Implementation Details:\n" +
			"- WebSocket-based real-time communication layer with automatic reconnection\n" +
			"- CRDT (Conflict-free Replicated Data Type) implementation for conflict resolution\n" +
			"- Optimistic UI updates with rollback capability\n" +
			"- Compression of real-time updates to minimize bandwidth usage\n" +
			"- Local operation queue for offline changes\n" +
			"- Distributed consensus algorithm for conflict resolution\n" +
			"- Vector clocks for causal ordering of operations\n" +
			"- Merkle tree-based version control system\n" +
			"- Binary delta encoding for efficient updates\n" +
			"- State synchronization protocol with automatic recovery\n\n" +
			"Performance Considerations:\n" +
			"- Batch processing of operations to reduce network overhead\n" +
			"- Intelligent throttling of updates based on network conditions\n" +
			"- Caching layer for frequently accessed document sections\n" +
			"- Progressive loading of document history\n" +
			"- Memory-efficient data structures for large documents\n" +
			"- Lazy loading of document sections\n" +
			"- Background processing of heavy operations\n" +
			"- Adaptive compression based on content type\n" +
			"- Connection quality-aware update frequency\n" +
			"- Resource usage monitoring and optimization\n\n" +
			"User Experience Enhancements:\n" +
			"- Visual indicators for concurrent editing conflicts\n" +
			"- Granular permission controls for document sections\n" +
			"- Activity feed showing recent changes and collaborators\n" +
			"- Comment threading tied to specific document sections\n" +
			"- Version history with selective rollback capabilities\n" +
			"- Collaborative cursors with user avatars\n" +
			"- Real-time presence indicators\n" +
			"- Inline conflict resolution UI\n" +
			"- Collaborative drawing tools\n" +
			"- Rich text formatting with conflict awareness\n\n" +
			"Security and Compliance:\n" +
			"- End-to-end encryption for all real-time communications\n" +
			"- Audit logging of all collaborative actions\n" +
			"- Role-based access control for document sections\n" +
			"- Compliance with data residency requirements\n" +
			"- GDPR-compliant data handling for collaborative content\n" +
			"- SOC 2 compliance for data handling\n" +
			"- HIPAA compliance for healthcare data\n" +
			"- Regular security audits and penetration testing\n" +
			"- Data retention and deletion policies\n" +
			"- Secure key rotation and management\n\n" +
			"Integration Capabilities:\n" +
			"- REST API for external system integration\n" +
			"- Webhook support for event notifications\n" +
			"- OAuth 2.0 authentication integration\n" +
			"- Custom plugin architecture\n" +
			"- Third-party service connectors\n" +
			"- Export/import functionality\n" +
			"- Custom workflow automation\n" +
			"- External storage system integration\n" +
			"- Analytics and monitoring integration\n" +
			"- Custom extension points\n\n" +
			"Monitoring and Analytics:\n" +
			"- Real-time performance metrics\n" +
			"- User engagement analytics\n" +
			"- Collaboration patterns analysis\n" +
			"- System health monitoring\n" +
			"- Error tracking and reporting\n" +
			"- Usage statistics and trends\n" +
			"- Performance bottleneck detection\n" +
			"- User behavior analytics\n" +
			"- Resource utilization tracking\n" +
			"- Custom metric collection\n\n" +
			"Future Considerations:\n" +
			"- AI-powered conflict resolution\n" +
			"- Predictive collaboration features\n" +
			"- Advanced document versioning\n" +
			"- Enhanced mobile collaboration\n" +
			"- Voice and video integration\n" +
			"- AR/VR collaboration support\n" +
			"- Blockchain-based version control\n" +
			"- Advanced machine learning features\n" +
			"- Cross-platform synchronization\n" +
			"- Enterprise-grade scalability",
		status: "In Progress",
		priority: "high",
		category: "core_features",
		created_at: 1672531200, // 2023-01-01
		target_quarter: "2024-Q1",
		owner: {
			id: "user-123",
			name: "Sarah Chen",
			profilePicture: "",
		},
		metadata: {
			comment_count: 24,
			last_updated: 1709251200, // 2024-03-01
			tags: ["collaboration", "real-time", "sync"],
		},
		upvotes: [],
	},
	{
		id: "feat-002",
		title: "Dark Mode Support",
		description:
			"Implement a comprehensive dark mode system that enhances user experience and reduces eye strain. This feature will include:\n\n" +
			"- System-wide dark theme with carefully selected color palettes\n" +
			"- Automatic theme switching based on system preferences\n" +
			"- Custom theme builder allowing users to create and share themes\n" +
			"- Accessibility considerations for color contrast and readability\n" +
			"- Smooth transitions between light and dark modes\n\n" +
			"The implementation will follow WCAG 2.1 guidelines for contrast ratios and ensure all UI components are properly themed. Users will be able to customize individual components and save their preferences across devices.",
		status: "Completed",
		priority: "medium",
		category: "ui_improvements",
		created_at: 1675209600, // 2023-02-01
		owner: {
			id: "user-123",
			name: "Alex Rivera",
			profilePicture: "",
		},
		metadata: {
			comment_count: 12,
			last_updated: 1704067200, // 2024-01-01
			tags: ["ui", "theme", "accessibility"],
		},
		upvotes: [],
	},
	{
		id: "feat-003",
		title: "API Rate Limiting",
		description:
			"Implement a sophisticated API rate limiting system to protect our infrastructure and ensure fair usage. This feature will include:\n\n" +
			"- Dynamic rate limiting based on user tier and historical usage\n" +
			"- Intelligent quota management with burst allowances\n" +
			"- Real-time monitoring and alerting for abuse detection\n" +
			"- Granular control over different API endpoints\n" +
			"- Detailed usage analytics and reporting\n\n" +
			"The system will implement token bucket algorithm with configurable parameters per endpoint. Enterprise customers will have higher limits and dedicated support for quota increases. The implementation will include proper error handling and clear rate limit headers in responses.",
		status: "Planned",
		priority: "high",
		category: "infrastructure",
		created_at: 1677628800, // 2023-03-01
		target_quarter: "2024-Q2",
		metadata: {
			comment_count: 8,
			last_updated: 1709251200, // 2024-03-01
			tags: ["api", "security", "performance"],
		},
		upvotes: [],
	},
	{
		id: "feat-004",
		title: "Mobile App Support",
		description:
			"Develop native mobile applications for iOS and Android platforms to extend our product's reach and functionality. This feature will include:\n\n" +
			"- Native iOS and Android applications with platform-specific optimizations\n" +
			"- Offline-first architecture with robust sync capabilities\n" +
			"- Push notifications for real-time updates and collaboration\n" +
			"- Biometric authentication and secure data storage\n" +
			"- Optimized UI/UX for touch interfaces and mobile workflows\n\n" +
			"The apps will maintain feature parity with the web version while taking advantage of native capabilities. We'll implement proper app store guidelines compliance, analytics tracking, and crash reporting. The development will follow a phased approach, starting with core features and expanding based on user feedback.",
		status: "On Hold",
		priority: "medium",
		category: "platform_expansion",
		created_at: 1680307200, // 2023-04-01
		metadata: {
			comment_count: 45,
			last_updated: 1704067200, // 2024-01-01
			tags: ["mobile", "ios", "android"],
		},
		upvotes: [],
	},
	{
		id: "feat-005",
		title: "Advanced Analytics Dashboard",
		description:
			"Create a comprehensive analytics dashboard that provides deep insights into product usage and performance. This feature will include:\n\n" +
			"- Custom report builder with drag-and-drop interface\n" +
			"- Real-time data visualization with interactive charts\n" +
			"- Advanced filtering and segmentation capabilities\n" +
			"- Export functionality for various formats (CSV, PDF, Excel)\n" +
			"- Automated report scheduling and distribution\n\n" +
			"The dashboard will integrate with our existing data pipeline and provide both high-level overviews and detailed drill-down capabilities. Users will be able to create custom metrics, set up alerts, and share insights with team members. The system will include role-based access control and audit logging for sensitive data.",
		status: "In Progress",
		priority: "high",
		category: "analytics",
		created_at: 1682899200, // 2023-05-01
		target_quarter: "2024-Q2",
		owner: {
			id: "user-123",
			name: "Maria Garcia",
			profilePicture: "",
		},
		metadata: {
			comment_count: 28,
			last_updated: 1709251200, // 2024-03-01
			tags: ["analytics", "reporting", "dashboard"],
		},
		upvotes: [],
	},
	{
		id: "feat-006",
		title: "SSO Integration",
		description:
			"Implement enterprise-grade Single Sign-On (SSO) integration to enhance security and simplify user authentication. This feature will include:\n\n" +
			"- Support for SAML 2.0 and OAuth 2.0 protocols\n" +
			"- Integration with major identity providers (Okta, Azure AD, Google Workspace)\n" +
			"- Just-in-time provisioning and deprovisioning\n" +
			"- Role-based access control mapping\n" +
			"- Audit logging and compliance reporting\n\n" +
			"The implementation will follow security best practices and support modern authentication standards. Enterprise customers will have access to advanced features like custom domain verification, IP restrictions, and session management. The system will include comprehensive documentation and support for common SSO configurations.\n\n" +
			"Authentication Protocols and Standards:\n" +
			"- SAML 2.0 with SP-initiated and IdP-initiated flows\n" +
			"- OAuth 2.0 with PKCE and refresh token rotation\n" +
			"- OpenID Connect with dynamic client registration\n" +
			"- WS-Federation for legacy enterprise systems\n" +
			"- SCIM 2.0 for user provisioning\n" +
			"- LDAP/Active Directory integration\n" +
			"- Kerberos authentication support\n" +
			"- Multi-factor authentication (MFA) integration\n" +
			"- WebAuthn/FIDO2 support\n" +
			"- Certificate-based authentication\n\n" +
			"Identity Provider Integrations:\n" +
			"- Okta with custom SAML attributes\n" +
			"- Azure AD with conditional access policies\n" +
			"- Google Workspace with domain-wide delegation\n" +
			"- OneLogin with custom provisioning rules\n" +
			"- Ping Identity with advanced federation\n" +
			"- Auth0 with custom rules and actions\n" +
			"- Keycloak with custom themes\n" +
			"- ForgeRock with policy enforcement\n" +
			"- Custom identity provider support\n" +
			"- Multiple IdP configuration\n\n" +
			"Security Features:\n" +
			"- End-to-end encryption of all authentication flows\n" +
			"- Certificate pinning for IdP connections\n" +
			"- Advanced session management\n" +
			"- IP-based access restrictions\n" +
			"- Device fingerprinting\n" +
			"- Risk-based authentication\n" +
			"- Brute force protection\n" +
			"- Account lockout policies\n" +
			"- Password policies enforcement\n" +
			"- Security headers implementation\n\n" +
			"Enterprise Features:\n" +
			"- Custom domain verification\n" +
			"- Organization-specific branding\n" +
			"- Custom attribute mapping\n" +
			"- Role-based access control\n" +
			"- Group synchronization\n" +
			"- Department-based policies\n" +
			"- Custom provisioning workflows\n" +
			"- Audit trail and logging\n" +
			"- Compliance reporting\n" +
			"- SLA monitoring\n\n" +
			"User Experience:\n" +
			"- Seamless login experience\n" +
			"- Customizable login pages\n" +
			"- Progressive profile completion\n" +
			"- Self-service account management\n" +
			"- Password reset workflows\n" +
			"- Account recovery options\n" +
			"- Session timeout handling\n" +
			"- Remember me functionality\n" +
			"- Mobile-optimized flows\n" +
			"- Accessibility compliance\n\n" +
			"Monitoring and Analytics:\n" +
			"- Real-time authentication monitoring\n" +
			"- Failed login tracking\n" +
			"- User activity analytics\n" +
			"- Performance metrics\n" +
			"- Error rate monitoring\n" +
			"- Usage patterns analysis\n" +
			"- Security event correlation\n" +
			"- Compliance reporting\n" +
			"- SLA monitoring\n" +
			"- Custom metric collection\n\n" +
			"Future Roadmap:\n" +
			"- Passwordless authentication\n" +
			"- Biometric authentication\n" +
			"- Blockchain-based identity\n" +
			"- Zero Trust architecture\n" +
			"- AI-powered security\n" +
			"- Advanced threat detection\n" +
			"- Cross-platform SSO\n" +
			"- Mobile app integration\n" +
			"- IoT device authentication\n" +
			"- Quantum-resistant cryptography",
		status: "Planned",
		priority: "high",
		category: "security",
		created_at: 1685577600, // 2023-06-01
		target_quarter: "2024-Q3",
		metadata: {
			comment_count: 15,
			last_updated: 1709251200, // 2024-03-01
			tags: ["security", "authentication", "enterprise"],
		},
		upvotes: [],
	},
	{
		id: "feat-007",
		title: "Offline Mode",
		description:
			"Implement a robust offline mode that allows users to continue working without an internet connection. This feature will include:\n\n" +
			"- Local data storage with encryption\n" +
			"- Automatic conflict resolution on reconnection\n" +
			"- Background sync with progress indicators\n" +
			"- Offline-first architecture for core features\n" +
			"- Bandwidth optimization for sync operations\n\n" +
			"The system will use IndexedDB for local storage and implement a queue-based sync mechanism. Users will be able to configure sync preferences and monitor sync status. The implementation will handle edge cases like storage limits, partial syncs, and version conflicts while maintaining data integrity.",
		status: "Planned",
		priority: "medium",
		category: "core_features",
		created_at: 1688169600, // 2023-07-01
		target_quarter: "2024-Q4",
		metadata: {
			comment_count: 12,
			last_updated: 1709251200, // 2024-03-01
			tags: ["offline", "sync", "mobile"],
		},
		upvotes: [],
	},
];

export interface RoadmapItemUpvote {
	id: string;
	linkedRecordId: string;
	createdAt: number;
	user: {
		id: string;
		name: string;
		profilePicture: string;
	};
}

export const roadmapItemUpvotes: RoadmapItemUpvote[] = [
	{
		id: "uv-001",
		user: {
			id: "user-123",
			name: "Emma Thompson",
			profilePicture: "",
		},
		linkedRecordId: "feat-001",
		createdAt: 1704067200, // 2024-01-01
	},
	{
		id: "uv-002",
		user: {
			id: "user-456",
			name: "James Wilson",
			profilePicture: "",
		},
		linkedRecordId: "feat-001",
		createdAt: 1704153600, // 2024-01-02
	},
	{
		id: "uv-003",
		user: {
			id: "user-789",
			name: "Sophia Chen",
			profilePicture: "",
		},
		linkedRecordId: "feat-004",
		createdAt: 1704240000, // 2024-01-03
	},
	{
		id: "uv-004",
		user: {
			id: "user-123",
			name: "Emma Thompson",
			profilePicture: "",
		},
		linkedRecordId: "feat-005",
		createdAt: 1704326400, // 2024-01-04
	},
	{
		id: "uv-005",
		user: {
			id: "user-456",
			name: "James Wilson",
			profilePicture: "",
		},
		linkedRecordId: "feat-004",
		createdAt: 1704412800, // 2024-01-05
	},
	{
		id: "uv-006",
		user: {
			id: "user-789",
			name: "Sophia Chen",
			profilePicture: "",
		},
		linkedRecordId: "feat-001",
		createdAt: 1704499200, // 2024-01-06
	},
	{
		id: "uv-007",
		user: {
			id: "user-123",
			name: "Emma Thompson",
			profilePicture: "",
		},
		linkedRecordId: "feat-004",
		createdAt: 1704585600, // 2024-01-07
	},
	{
		id: "uv-008",
		user: {
			id: "user-456",
			name: "James Wilson",
			profilePicture: "",
		},
		linkedRecordId: "feat-005",
		createdAt: 1704672000, // 2024-01-08
	},
	{
		id: "uv-009",
		user: {
			id: "user-123",
			name: "Emma Thompson",
			profilePicture: "",
		},
		linkedRecordId: "feat-006",
		createdAt: 1704758400, // 2024-01-09
	},
	{
		id: "uv-010",
		user: {
			id: "user-789",
			name: "Sophia Chen",
			profilePicture: "",
		},
		linkedRecordId: "feat-005",
		createdAt: 1704844800, // 2024-01-10
	},
];
